import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import CreateTrip from '../TripModal/Trips/CreateTrip/CreateTripMC'

import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'

import LineBoltAltIcon from 'LuxKit/Icons/line/LineBoltAltIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from './Common/Tile/TileCard'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import LeIcon from '../Icons/LeIcon'
import type { FullTrip } from 'tripPlanner/types/common'
import { getTripURL } from 'tripPlanner/utils'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import * as Analytics from 'analytics/analytics'

export const TRIP_GENERATION_FLOWS = {
  SURPRISE_ME: 'SurpriseMe',
  CUSTOMISE: 'Customise',
}

export type TripGenerationFlow = typeof TRIP_GENERATION_FLOWS[keyof typeof TRIP_GENERATION_FLOWS]

interface Props {
  hotelCity: string
  isGeneratingTrip: boolean
  handleSurpriseMe: () => void
  handleCustomise: () => void
  onGenerateTripFlowSelect: (flow: TripGenerationFlow) => void
}

function AITripStartSelection(props: Props) {
  const intl = useIntl()
  const {
    hotelCity,
    isGeneratingTrip,
    handleSurpriseMe,
    handleCustomise,
    onGenerateTripFlowSelect,
  } = props

  const handleCardClick = useCallback((value: TripGenerationFlow) => {
    switch (value) {
      case TRIP_GENERATION_FLOWS.SURPRISE_ME:
        Analytics.trackClientEvent({
          subject: 'ready_made_trip',
          action: 'click',
          category: 'trip_planner',
          type: 'interaction',
        })
        onGenerateTripFlowSelect(TRIP_GENERATION_FLOWS.SURPRISE_ME)
        handleSurpriseMe()
        break
      case TRIP_GENERATION_FLOWS.CUSTOMISE:
        Analytics.trackClientEvent({
          subject: 'personalise_my_trip',
          action: 'click',
          category: 'trip_planner',
          type: 'interaction',
        })
        onGenerateTripFlowSelect(TRIP_GENERATION_FLOWS.CUSTOMISE)
        handleCustomise()
        break
    }
  }, [handleCustomise, handleSurpriseMe, onGenerateTripFlowSelect])

  const showModal = useContext(ModalContext)
  const navigateTo = useNavigateTo()
  const modalElement = useModalElementContext()

  const onCreateTrip = useCallback(
    (trip: FullTrip) => {
      navigateTo(getTripURL(trip.id))
      modalElement.resolve()
    },
    [modalElement, navigateTo],
  )

  const createNewEmptyTrip = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'start_from_scratch',
      action: 'click',
      category: 'trip_planner',
      type: 'interaction',
    })
    showModal(<CreateTrip onCreateTrip={onCreateTrip} />)
  }, [showModal, onCreateTrip])

  const handleCreateNewEmptyTrip = usePendingLoginHandler(
    createNewEmptyTrip,
    'tripPlannerPagesLogin',
  )

  const headingText = intl.formatMessage(
    {
      id: 'qzBLBtDLm0',
      defaultMessage: 'Let’s build your <i>{hotelCity}</i> trip',
      description: 'Heading text for AI trip start selection with italicized city name',
    },
    {
      hotelCity,
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'aNeZZN1fT5',
    defaultMessage: 'We’ll create your itinerary in seconds using smart AI and expert travel knowledge.',
    description: 'Description text explaining how the AI trip planner works',
  })

  const personaliseTitle = intl.formatMessage({
    id: '/AnRFYjS/x',
    defaultMessage: 'Personalise my trip',
    description: 'Title for personalise trip option',
  })

  const personaliseSubtitle = intl.formatMessage({
    id: 'AcX+Mc1Cy4',
    defaultMessage: 'Tailored to your preferences, budget, and must-see spots.',
    description: 'Subtitle for personalise trip option',
  })

  const recommendedLabel = intl.formatMessage({
    id: 'CuGFmFyILC',
    defaultMessage: 'Recommended',
    description: 'Label text for recommended option',
  })

  const readyMadeTitle = intl.formatMessage({
    id: 'dJBVJoy75B',
    defaultMessage: 'Ready-Made Trip',
    description: 'Title for ready-made trip option',
  })

  const readyMadeSubtitle = intl.formatMessage({
    id: 'm8YtQsMVml',
    defaultMessage: 'A pre-planned itinerary featuring popular attractions.',
    description: 'Subtitle for ready-made trip option',
  })

  const startFromScratchText = intl.formatMessage({
    id: 'ASmNzZkNY2',
    defaultMessage: 'Start from scratch',
    description: 'Button text to start trip planning from scratch',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={40}>
      <Group direction="horizontal" horizontalAlign="start" gap={8}>
        <LeIcon size="M" />
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'vETPjJ/mLs',
            defaultMessage: 'TripPlanner',
            description: 'Brand name label for TripPlanner feature',
          })}
        </BodyText>
      </Group>
      <Group direction="vertical" gap={8}>

        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </Group>

      <TileCardsGroup>
        <TileCard
          title={personaliseTitle}
          subtitle={personaliseSubtitle}
          icon={<SolidSparklesIcon />}
          statusLabel={{ text: recommendedLabel, variant: 'confirmation', hideOnMobile: true }}
          onClick={handleCardClick}
          value={TRIP_GENERATION_FLOWS.CUSTOMISE}
          disabled={isGeneratingTrip}
          interactionIcon="right"

        />
        <TileCard
          title={readyMadeTitle}
          subtitle={readyMadeSubtitle}
          icon={<LineBoltAltIcon />}
          onClick={handleCardClick}
          value={TRIP_GENERATION_FLOWS.SURPRISE_ME}
          disabled={isGeneratingTrip}
          interactionIcon="right"
        />
      </TileCardsGroup>
      <Group direction="vertical" horizontalAlign="start" gap={20}>
        <CSSBreakpoint min="tablet">
          <TextButton
            kind="secondary"
            variant="dark"
            onClick={handleCreateNewEmptyTrip}
            endIcon={<LineAngleRightIcon />}
            fit="mobile-full-width"
          >
            {startFromScratchText}
          </TextButton>
        </CSSBreakpoint>
      </Group>
    </Group>
  )
}

export default AITripStartSelection
